import { useNavigate } from "@tanstack/react-router";
import { ClipboardList, Lock, Plus } from "lucide-react";
import { useId, useState } from "react";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { interactiveRowProps } from "@/components/shared/interactive-row";
import { SearchInput } from "@/components/shared/search-input";
import { StatusPill, type StatusTone } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import {
  type ReviewTemplateSummary,
  useCreateReviewTemplate,
  useReviewTemplates,
} from "@/hooks/use-review-templates";

/**
 * Review forms — the list (EPIC-047).
 *
 * A "form" is the question set a cycle generates from: which criteria are
 * rated, who is asked to rate them, and which roles and levels it covers.
 */

const STATUS_TONES: Record<ReviewTemplateSummary["status"], StatusTone> = {
  draft: "neutral",
  active: "success",
  archived: "warning",
};

export function ReviewFormsList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading, isError, error, refetch } = useReviewTemplates({
    search: debouncedSearch || undefined,
  });

  if (isError) {
    return (
      <ErrorState
        message={
          error instanceof Error
            ? `Couldn't load review forms: ${error.message}`
            : "Couldn't load review forms"
        }
        onRetry={() => refetch()}
      />
    );
  }

  const templates = data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <SearchInput
          value={search}
          onValueChange={setSearch}
          placeholder="Search review forms"
          className="max-w-xs"
        />
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="size-4" aria-hidden />
          New form
        </Button>
      </div>

      <DataTable
        headers={[
          { label: "Form" },
          { label: "Status" },
          { label: "Extra questions" },
          { label: "Roles covered" },
          { label: "Reviewers asked" },
        ]}
        loading={isLoading}
        empty={
          <EmptyState
            icon={ClipboardList}
            title="No review forms yet"
            description="A form sets out what people are rated on and who is asked. Create one to get started."
          />
        }
      >
        {templates.map((template) => (
          <tr
            key={template.id}
            {...interactiveRowProps(
              () =>
                navigate({
                  to: "/dashboard/reviews/forms/$templateId",
                  params: { templateId: template.id },
                }),
              `Open ${template.name}`,
            )}
          >
            <td className="px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="font-medium">{template.name}</span>
                {template.lockedAt && (
                  <Lock className="size-3.5 text-muted-foreground" aria-label="Locked" />
                )}
              </div>
              {template.description && (
                <p className="mt-0.5 line-clamp-1 text-sm text-muted-foreground">
                  {template.description}
                </p>
              )}
            </td>
            <td className="px-4 py-3">
              <StatusPill tone={STATUS_TONES[template.status]} label={template.status} />
            </td>
            <td className="px-4 py-3 tabular-nums">{template.criterionCount}</td>
            <td className="max-w-[22rem] px-4 py-3">
              <SummaryCell
                value={template.scopeSummary}
                empty="No roles yet — this form is never matched"
              />
            </td>
            <td className="max-w-[16rem] px-4 py-3">
              <SummaryCell value={template.relationshipSummary} empty="Nobody asked yet" />
            </td>
          </tr>
        ))}
      </DataTable>

      <CreateReviewFormDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}

/**
 * A named list where a count used to be.
 *
 * Empty is the case that matters: a form covering no role is never matched to
 * anybody, and a form asking nobody generates no task — both look like
 * ordinary rows if all you show is "0", so each says what its emptiness means.
 */
function SummaryCell({ value, empty }: { value: string | null; empty: string }) {
  if (!value) {
    return <span className="text-muted-foreground text-sm italic">{empty}</span>;
  }
  return (
    <span className="line-clamp-2 text-sm" title={value}>
      {value}
    </span>
  );
}

/**
 * Creating a form asks for a name and nothing else — criteria, roles and
 * reviewers are all chosen on the form itself, where they can be seen together.
 */
function CreateReviewFormDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const nameId = useId();
  const descriptionId = useId();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const create = useCreateReviewTemplate();

  const submit = async () => {
    if (!name.trim()) return;
    const created = await create.mutateAsync({
      name: name.trim(),
      description: description.trim() || null,
      status: "draft",
    });
    onOpenChange(false);
    setName("");
    setDescription("");
    navigate({ to: "/dashboard/reviews/forms/$templateId", params: { templateId: created.id } });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New review form</DialogTitle>
          <DialogDescription>
            Give it a name you'll recognise later — "Engineering — mid-year", say. You'll choose
            what it asks next.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor={nameId}>Name</Label>
            <Input
              id={nameId}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Engineering — mid-year"
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={descriptionId}>Description (optional)</Label>
            <Textarea
              id={descriptionId}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!name.trim() || create.isPending}>
            {create.isPending ? "Creating…" : "Create form"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
